import { useRef, useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Lock, ChevronsRight, Heart } from 'lucide-react';

export const EntryGate = ({ coverImage, onUnlock }) => {
    const trackRef = useRef(null);
    const [maxDrag, setMaxDrag] = useState(0);
    const [unlocked, setUnlocked] = useState(false);
    const x = useMotionValue(0);

    // Hint text fades as user drags
    const hintOpacity = useTransform(x, [0, 30, 80], [1, 0.6, 0]);
    // Track thumb width (40px) when calculating fill
    const fillWidth = useTransform(x, (v) => `${Math.min(100, ((v + 48) / (maxDrag + 48)) * 100)}%`);

    useEffect(() => {
        const calc = () => {
            if (trackRef.current) {
                // track width - thumb width (40) - left offset (4) - right padding (4)
                setMaxDrag(trackRef.current.offsetWidth - 40 - 8);
            }
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, []);

    const handleDragEnd = () => {
        if (unlocked) return;
        const current = x.get();
        if (current >= maxDrag - 10) {
            setUnlocked(true);
            animate(x, maxDrag, { duration: 0.15 });
            setTimeout(() => onUnlock(), 450);
        } else {
            animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
        }
    };

    return (
        <motion.div
            data-testid="entry-gate"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 overflow-hidden bg-[#0a0608]"
        >
            {/* Full-bleed cover */}
            <img
                src={coverImage}
                alt="Kelltchup"
                className="absolute inset-0 w-full h-full object-cover object-[center_28%]"
                style={{
                    filter: 'brightness(1.08) contrast(1.18) saturate(1.2)',
                    imageRendering: 'auto',
                }}
                fetchpriority="high"
                decoding="async"
                data-testid="entry-gate-cover"
            />

            {/* Subtle overlay for legibility - photo stays very visible */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40 pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-[28%] bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none" />

            {/* Online badge */}
            <div className="absolute top-6 left-5 z-10">
                <div className="flex items-center gap-2 bg-black/45 backdrop-blur-md rounded-full px-3.5 py-1.5 border border-white/10">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d62828] opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d62828]" />
                    </span>
                    <span className="text-white/95 text-xs font-medium tracking-wide">online agora</span>
                </div>
            </div>

            {/* Content panel at bottom - more compact, less intrusive */}
            <div className="absolute inset-x-0 bottom-0 px-4 pb-5 pt-10">
                <div className="relative max-w-md mx-auto rounded-2xl bg-black/35 backdrop-blur-md border border-white/10 px-5 pt-3 pb-3.5 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)]">
                    {/* Greeting */}
                    <h1
                        data-testid="entry-gate-greeting"
                        className="text-center text-white text-[2rem] leading-tight tracking-tight"
                        style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive" }}
                    >
                        Oi mozin <Heart className="inline-block align-middle w-7 h-7 -mt-1 ml-1 text-black fill-black" strokeWidth={2.5} style={{ filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.6))' }} />
                    </h1>
                    <p className="text-center text-white/70 text-xs mt-1 leading-relaxed">
                        antes de entrar, preciso saber se é você mesmo...
                    </p>

                    {/* Slider track */}
                    <div
                        ref={trackRef}
                        className="relative mt-4 h-12 rounded-full bg-white/8 border border-white/15 overflow-hidden select-none"
                        data-testid="entry-gate-slider-track"
                    >
                        {/* Filled progress */}
                        <motion.div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#d62828]/70 to-[#b91c1c]/70"
                            style={{ width: fillWidth }}
                        />

                        {/* Hint text */}
                        <motion.div
                            className="absolute inset-0 flex items-center justify-center gap-1 pointer-events-none text-white/90 text-sm"
                            style={{ opacity: hintOpacity }}
                        >
                            <span className="font-light tracking-wide">deslize para entrar</span>
                            <ChevronsRight className="w-4 h-4 animate-pulse" />
                        </motion.div>

                        {/* Draggable thumb */}
                        <motion.div
                            data-testid="entry-gate-slider-thumb"
                            drag="x"
                            dragConstraints={{ left: 0, right: maxDrag }}
                            dragElastic={0}
                            dragMomentum={false}
                            onDragEnd={handleDragEnd}
                            style={{ x }}
                            whileTap={{ scale: 0.95 }}
                            className="absolute top-1 left-1 h-10 w-10 rounded-full bg-white flex items-center justify-center shadow-[0_4px_18px_rgba(251,113,133,0.55)] cursor-grab active:cursor-grabbing"
                        >
                            <Lock className="w-4 h-4 text-[#d62828]" strokeWidth={2.5} />
                        </motion.div>
                    </div>

                    {/* Subtitle */}
                    <p className="text-center text-white/40 text-[0.7rem] mt-3 italic tracking-wide">
                        Descubra meus segredinhos...
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

export default EntryGate;
